import { useMutation, useQuery, useQueryClient, UseQueryResult } from "react-query"
import { getActiveVendorsList, getQboVendorsList, getVendorById, getVendorsList, importVendors, removeVendor, saveVendor } from "./vendorsApi"
import { QboVendor, Vendor, VendorItem } from "./vendorsModel"

enum VendorQueryKeyNames {
  VENDORS_LIST = 'vendorsList',
  QBO_VENDORS_LIST = 'qboVendors',
  VENDOR = 'vendor'
}

export const useVendorsListQuery = (): UseQueryResult<VendorItem[]> => {
  return useQuery([VendorQueryKeyNames.VENDORS_LIST], async () => getVendorsList(), {
    refetchOnWindowFocus: false
  })
}

export const useActiveVendorsListQuery = (enabled: boolean): UseQueryResult<VendorItem[]> => {
  return useQuery(['activeVendorsList'], async () => getActiveVendorsList(), {
    enabled,
    refetchOnWindowFocus: false
  })
}

export const useVendorsListQueryInvalidator = () => {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries([VendorQueryKeyNames.VENDORS_LIST])
}

export const useQboVendorsListQuery = (unassignedOnly: boolean): UseQueryResult<QboVendor[]> => {
  return useQuery([VendorQueryKeyNames.QBO_VENDORS_LIST, { onlyShowUnassigned: unassignedOnly }], 
    async () => getQboVendorsList(unassignedOnly)
  )
}

export const useVendor = (id?: number): UseQueryResult<Vendor> => {
  return useQuery({
    queryKey: [VendorQueryKeyNames.VENDOR, id],
    queryFn: async () => getVendorById(id),
    enabled: !!id,
  })
}

export const useSaveVendorMutation = (onSuccess?: (vendor: Vendor) => void) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ vendor }: { vendor: Vendor }) => {
      const result = await saveVendor(vendor)
      return result
    },
    onSuccess: (vendor) => {
      queryClient.invalidateQueries([VendorQueryKeyNames.VENDORS_LIST])
      queryClient.setQueryData([VendorQueryKeyNames.VENDOR, vendor.id], vendor)
      onSuccess?.(vendor)
    }
  })
}

export const useImportVendorsMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: QboVendor[]) => {
      await importVendors(payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries([VendorQueryKeyNames.VENDORS_LIST])
      onSuccess?.()
    }
  })
}

export const useRemoveVendorMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: removeVendor,
    onSuccess: () => {
      queryClient.invalidateQueries([VendorQueryKeyNames.VENDORS_LIST])
      onSuccess?.()
    }
  })
}